import React, { useEffect, useMemo, useRef, useState } from "react";
import { motion } from "framer-motion";
import { useParams } from "react-router-dom";
import { Lightning, MusicNotes, GoogleLogo, SignOut } from "@phosphor-icons/react";
import { api, fileUrl } from "../lib/api";
import { findSlotForDeeplink, slugify } from "../lib/slug";
import SlotCard from "../components/SlotCard";
import ContactForm from "../components/ContactForm";
import StartShoowSplash from "../components/StartShoowSplash";
import MarqueeTicker from "../components/MarqueeTicker";
import LanguageSwitcher from "../components/LanguageSwitcher";
import ThemeToggle from "../components/ThemeToggle";
import LatestVideos from "../components/LatestVideos";
import useDocumentMeta from "../hooks/useDocumentMeta";
import { usePlayer } from "../context/PlayerContext";
import { useAuth } from "../context/AuthContext";
import { startGoogleSignIn } from "../lib/googleSignIn";

const DEFAULT_CONTENT = {
  hero_overline: "The Undaground Shoow",
  hero_title_part1: "Top",
  hero_title_part2: "Ten",
  hero_title_part3: "Songs.",
  hero_title_accent: "UGShoow Edition.",
  hero_subtitle: "From the streets to your speakers.",
  section_label: "The Countdown",
  section_heading: "Ten Heaters. No Skips.",
  section_meta: "#01 → #10 · continuous play",
  footer_text: "Undaground Shoow — Live from the streets.",
  heading_font: "Outfit",
  body_font: "Manrope",
  base_font_scale: 1.0,
  heading_color: "#ffffff",
  body_color: "#a1a1aa",
  accent_color: "#ff8a3c",
};

export default function PublicShowcase() {
  const { rank: deepRank, slug: deepSlug } = useParams();
  const { user, logout } = useAuth();
  const [slots, setSlots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [content, setContent] = useState(DEFAULT_CONTENT);
  const { setQueue, currentTrack, currentIndex, isMuted, isPlaying, toggleMute, play } = usePlayer();
  const lastScrolledRankRef = useRef(null);
  const scrollTriggeredRef = useRef(false);
  const visitLoggedRef = useRef(false);
  const lastPlayedRankRef = useRef(null);
  const deepLinkApplied = useRef(false);

  // Load slots + site content + log one visit per session
  useEffect(() => {
    let mounted = true;
    Promise.all([api.get("/slots"), api.get("/site-content").catch(() => ({ data: DEFAULT_CONTENT }))]).then(
      ([slotsRes, contentRes]) => {
        if (!mounted) return;
        const list = slotsRes.data || [];
        setSlots(list);
        setContent({ ...DEFAULT_CONTENT, ...(contentRes.data || {}) });
        setLoading(false);

        // Deep-link: /track/:rank/:slug — slug wins (song-stable). Falls back to rank.
        const target = findSlotForDeeplink(list, deepRank, deepSlug);
        const startIdx = target ? list.findIndex((s) => s.rank === target.rank) : -1;
        if (startIdx >= 0 && !deepLinkApplied.current) {
          deepLinkApplied.current = true;
          setQueue(list, { autoplayFirst: false });
          setTimeout(() => play(startIdx), 50);
        } else {
          setQueue(list, { autoplayFirst: true });
        }
      }
    ).catch(() => setLoading(false));

    if (!visitLoggedRef.current) {
      visitLoggedRef.current = true;
      api.post("/events", { kind: "visit" }).catch(() => {});
    }
    return () => { mounted = false; };
  }, [setQueue, play, deepRank, deepSlug]);

  // First scroll counts as user gesture — unmute + ensure playback
  useEffect(() => {
    const handleFirstScroll = () => {
      if (scrollTriggeredRef.current) return;
      scrollTriggeredRef.current = true;
      if (isMuted) toggleMute();
      if (!isPlaying) play();
    };
    window.addEventListener("scroll", handleFirstScroll, { passive: true, once: true });
    return () => window.removeEventListener("scroll", handleFirstScroll);
  }, [isMuted, isPlaying, toggleMute, play]);

  // Log a play event whenever the active index changes (every track start,
  // including loops back to the same rank after wrapping the queue).
  const currentRank = currentTrack?.rank;
  useEffect(() => {
    if (currentIndex < 0 || !currentRank) return;
    api.post("/events", { kind: "play", slot_rank: currentRank }).catch(() => {});
  }, [currentIndex, currentRank]);

  // Auto-scroll active card into view
  useEffect(() => {
    if (!currentTrack) return;
    if (lastScrolledRankRef.current === currentTrack.rank) return;
    lastScrolledRankRef.current = currentTrack.rank;
    const el = document.getElementById(`slot-${currentTrack.rank}`);
    if (!el) return;
    const rect = el.getBoundingClientRect();
    if (rect.top < 0 || rect.top > window.innerHeight * 0.5) {
      el.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, [currentTrack]);

  const artistNames = slots.map((s) => s.artist_name).filter(Boolean);

  // Per-track deeplink: build OG/Twitter meta tags so the URL unfurls with
  // cover art + track + artist on platforms that fetch dynamic meta.
  const deepSlot = useMemo(() => {
    return findSlotForDeeplink(slots, deepRank, deepSlug);
  }, [deepRank, deepSlug, slots]);

  const trackMeta = useMemo(() => {
    if (!deepSlot) return null;
    const origin = typeof window !== "undefined" ? window.location.origin : "";
    const songSlug = (deepSlot.artist_name && deepSlot.song_title)
      ? `${deepSlot.rank}/${slugify(deepSlot.artist_name, deepSlot.song_title)}`
      : `${deepSlot.rank}`;
    const canonical = `https://www.undagroundshoow.com/track/${songSlug}`;
    const url = `${origin}/track/${songSlug}`;
    const title = `#${deepSlot.rank} · ${deepSlot.song_title || "Untitled"} — ${deepSlot.artist_name || "Unknown"} | Undaground Shoow`;
    const desc = deepSlot.bio || `Hear #${deepSlot.rank} on the Undaground Shoow Top Ten countdown.`;
    const image = deepSlot.image_path ? fileUrl(deepSlot.image_path) : "";
    return {
      title,
      metas: [
        { property: "og:type", content: "music.song" },
        { property: "og:title", content: title },
        { property: "og:description", content: desc },
        { property: "og:url", content: url },
        ...(image ? [{ property: "og:image", content: image }] : []),
        { property: "music:musician", content: deepSlot.artist_name || "" },
        { name: "twitter:card", content: image ? "summary_large_image" : "summary" },
        { name: "twitter:title", content: title },
        { name: "twitter:description", content: desc },
        ...(image ? [{ name: "twitter:image", content: image }] : []),
      ],
      canonical,
    };
  }, [deepSlot]);

  useDocumentMeta(trackMeta || {});

  const headingFont = `'${content.heading_font}', sans-serif`;
  const bodyFont = `'${content.body_font}', sans-serif`;
  const scale = content.base_font_scale || 1.0;

  return (
    <div
      className="min-h-screen bg-ink-900 relative overflow-hidden pb-28"
      data-testid="public-showcase"
      style={{ fontFamily: bodyFont, fontSize: `${scale}rem`, color: content.body_color }}
    >
      <style>{`
        [data-testid="public-showcase"] h1,
        [data-testid="public-showcase"] h2,
        [data-testid="public-showcase"] h3 { font-family: ${headingFont}; color: ${content.heading_color}; }
      `}</style>

      <StartShoowSplash />

      {/* Sticky header — admin link removed; language switcher top-right */}
      <header className="sticky top-0 z-50 glass border-b border-white/5" data-testid="public-header">
        <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-12 h-16 flex items-center justify-between gap-2">
          <a href="/" className="flex items-center gap-2 group min-w-0" data-testid="header-logo">
            <Lightning size={22} weight="fill" style={{ color: content.accent_color }} className="group-hover:rotate-12 transition-transform shrink-0" />
            <span className="hidden sm:inline font-black tracking-tight text-lg whitespace-nowrap" style={{ color: content.heading_color }}>UNDAGROUND SHOOW</span>
            <span className="sm:hidden font-black tracking-tight text-base" style={{ color: content.heading_color }}>UGSHOOW</span>
          </a>
          <div className="flex items-center gap-2 sm:gap-3">
            <a href="#playlist" className="hidden md:inline text-zinc-400 hover:text-white transition-colors text-sm" data-testid="nav-playlist">The List</a>
            <a href="#contact" className="hidden md:inline text-zinc-400 hover:text-white transition-colors text-sm" data-testid="nav-contact">Labels</a>
            <ThemeToggle />
            <LanguageSwitcher />
            {user && user !== false ? (
              <div className="flex items-center gap-1.5" data-testid="public-user-chip">
                {user.picture ? (
                  <img src={user.picture} alt="" className="w-6 h-6 rounded-full ring-1 ring-ink-600" />
                ) : (
                  <div className="w-6 h-6 rounded-full bg-neon-cyan/20 border border-neon-cyan/40 flex items-center justify-center text-[10px] font-bold text-neon-cyan uppercase">
                    {(user.name || user.email || "?").charAt(0)}
                  </div>
                )}
                <span className="hidden md:inline text-xs text-zinc-400 font-mono max-w-[140px] truncate" data-testid="public-user-email">{user.email}</span>
                <button
                  onClick={logout}
                  className="p-1.5 rounded border border-ink-600 text-zinc-400 hover:text-neon-magenta hover:border-neon-magenta/50 transition-colors"
                  title="Sign out"
                  data-testid="public-signout-button"
                  aria-label="Sign out"
                >
                  <SignOut size={12} />
                </button>
              </div>
            ) : (
              <button
                onClick={() => startGoogleSignIn("/auth/callback")}
                className="inline-flex items-center gap-1.5 px-2 sm:px-2.5 py-1.5 rounded-md border border-ink-600 bg-ink-800/60 text-zinc-300 hover:text-white hover:border-white/40 transition-colors text-xs font-mono uppercase tracking-widest shrink-0"
                data-testid="public-google-signin"
                aria-label="Sign in with Google"
              >
                <GoogleLogo size={14} weight="bold" />
                <span className="hidden sm:inline">Sign in</span>
              </button>
            )}
            <a
              href="/login"
              className="inline-flex items-center gap-1.5 px-2 sm:px-2.5 py-1.5 rounded-md border border-ink-600 bg-ink-800/60 text-zinc-300 hover:text-neon-cyan hover:border-neon-cyan/50 transition-colors text-xs font-mono uppercase tracking-widest shrink-0"
              data-testid="nav-admin"
              aria-label="Admin"
            >
              <Lightning size={14} weight="fill" />
              <span className="hidden sm:inline">Admin</span>
            </a>
          </div>
        </div>
      </header>

      <MarqueeTicker artists={artistNames} />

      {deepSlot && (
        <div className="max-w-7xl mx-auto px-6 lg:px-12 pt-6" data-testid="track-deeplink-banner">
          <div className="rounded-lg border border-neon-cyan/30 bg-neon-cyan/[0.05] px-4 py-3 flex items-center justify-between gap-3 flex-wrap">
            <div className="text-xs sm:text-sm text-zinc-200">
              <span className="text-neon-cyan font-bold tracking-[0.2em] uppercase font-mono">Direct link · </span>
              Now playing <span className="text-white font-semibold">#{String(deepSlot.rank).padStart(2, "0")} {deepSlot.song_title}</span> by <span className="text-white">{deepSlot.artist_name}</span>.
            </div>
            <a href="/" className="text-xs font-mono uppercase tracking-widest text-zinc-400 hover:text-neon-cyan transition-colors" data-testid="track-back-to-countdown">← Back to countdown</a>
          </div>
        </div>
      )}

      <section
        id="playlist"
        className="relative max-w-7xl mx-auto px-6 lg:px-12 pt-12 lg:pt-16 pb-16 lg:pb-24 space-y-16 lg:space-y-24 grid-bg"
        data-testid="playlist-section"
      >
        <div className="absolute top-0 left-1/4 w-[500px] h-[500px] bg-neon-cyan/8 rounded-full blur-[140px] pointer-events-none -z-10" />
        <div className="absolute top-40 right-1/4 w-[400px] h-[400px] bg-neon-magenta/8 rounded-full blur-[140px] pointer-events-none -z-10" />

        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
          className="flex items-end justify-between flex-wrap gap-4 mb-2">
          <div>
            <p className="text-[10px] sm:text-xs font-bold tracking-[0.4em] uppercase mb-3 inline-flex items-center gap-2" style={{ color: content.accent_color }} data-testid="masthead-overline">
              <MusicNotes size={14} weight="fill" /> {content.hero_overline}
            </p>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter leading-[0.95] text-balance" data-testid="masthead-title">
              {content.hero_title_part1}{" "}
              <span className="italic font-light" style={{ color: content.accent_color }}>{content.hero_title_part2}</span>
              {" "}{content.hero_title_part3}
              <br className="hidden sm:block" />
              <span className="bg-gradient-to-r from-neon-cyan to-neon-magenta bg-clip-text text-transparent">{content.hero_title_accent}</span>
            </h1>
          </div>
          <p className="text-sm font-mono" style={{ color: content.body_color }} data-testid="masthead-meta">{content.section_meta}</p>
        </motion.div>

        {loading ? (
          <div className="space-y-12">
            {Array.from({ length: 3 }).map((_, i) => (
              <div key={`skeleton-${i}`} className="grid grid-cols-1 lg:grid-cols-12 gap-8 animate-pulse">
                <div className="lg:col-span-5 aspect-square bg-ink-800 rounded-md" />
                <div className="lg:col-span-7 space-y-4">
                  <div className="h-8 bg-ink-800 rounded w-3/4" />
                  <div className="h-4 bg-ink-800 rounded w-1/2" />
                  <div className="h-16 bg-ink-800 rounded" />
                </div>
              </div>
            ))}
          </div>
        ) : (
          slots.map((slot, idx) => <SlotCard key={slot.rank} slot={slot} index={idx} shareSlogan={content.share_slogan_template} shareSloganByPlatform={{ x: content.share_slogan_x, reddit: content.share_slogan_reddit, facebook: content.share_slogan_facebook, whatsapp: content.share_slogan_whatsapp }} shareHashtags={content.share_hashtags} shareButtonColor={content.share_button_color} />)
        )}
      </section>

      {/* YouTube channel videos — graceful empty state until channel URL is set */}
      <LatestVideos />

      {/* Artist self-submission CTA */}
      <section className="max-w-7xl mx-auto px-6 lg:px-12 py-16" data-testid="submit-cta">
        <div className="rounded-2xl border border-neon-cyan/30 bg-gradient-to-br from-neon-cyan/[0.08] via-ink-900 to-ink-900 p-8 sm:p-10 flex flex-col sm:flex-row items-start sm:items-center gap-6 justify-between">
          <div>
            <p className="text-xs font-bold tracking-[0.3em] text-neon-cyan uppercase mb-2">Get heard</p>
            <h3 className="text-2xl sm:text-3xl font-black tracking-tighter mb-2">Got a heater? Send it in.</h3>
            <p className="text-sm text-zinc-400 max-w-xl">
              Real artists, real shot. Submit your track, art, and links — if the streets feel it, you land on the next Top 10.
            </p>
          </div>
          <a
            href="/submit"
            className="inline-flex items-center gap-2 px-5 py-3 rounded-md bg-neon-cyan text-black text-sm font-bold tracking-wide hover:bg-neon-cyanHover whitespace-nowrap"
            data-testid="submit-cta-link"
          >
            <MusicNotes size={16} weight="fill" /> Submit your track
          </a>
        </div>
      </section>

      <ContactForm />

      {/* Footer — no admin link; private bookmarked /login only */}
      <footer className="border-t border-ink-600" data-testid="public-footer">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 py-10 flex items-center justify-center gap-4">
          <p className="text-xs font-mono tracking-widest text-zinc-500 uppercase text-center" data-testid="footer-text">
            {content.footer_text}
          </p>
        </div>
      </footer>
    </div>
  );
}
